import React, { useState, useEffect, useRef } from "react";

import * as am4core from "@amcharts/amcharts4/core";
import ContentCard from "../../common/template/contentCard";
import ContentCardBody from "../../common/template/contentCardBody";
import { montarGraficoClusteredColumnMensal } from "../../common/graficos/montarGraficoClusteredColumnMensal.js";
import { adicionarPontosMilhar } from "../../utils/adicionarPontosMilhar.js";
import { montarGraficoClusteredColumnAnual } from "../../common/graficos/montarGraficoClusteredColumnAnual.js";
import Row from "../../common/layout/row.jsx";
import Grid from "../../common/layout/grid.jsx";
import Button from "../../common/button/button.jsx";
import Select from "../../common/form/select.jsx";

export function GraficoEconomia(props) {
    function mapearSelect(lista = []) {
        return lista.map((item) => ({ label: item.valor, value: item.id }));
    }

    const cores = [am4core.color("#0084ff"), am4core.color("#fdff6a"), am4core.color("#f49e13"), am4core.color("#12ffb8"), am4core.color("#ff5e76"), am4core.color("#00d5ff")];

    const contentCardRef = useRef(null);
    const [exibirUCs, setExibirUCs] = useState(false);
    const [filtroUCs, setFiltroUCs] = useState(mapearSelect(props.ucFiltradaComRemotas));

    const [graficoEconomiaAnual, setGraficoEconomiaAnual] = useState(null);
    const [graficoEconomiaMensal, setGraficoEconomiaMensal] = useState(null);


    useEffect(() => {
        atualizarGraficosEconomia();
        return desmontarGrafico;
    }, [props.listaGraficoGeracaoEconomia, filtroUCs]);

    useEffect(() => {
        setFiltroUCs(mapearSelect(props.ucFiltradaComRemotas));
    }, [props.ucFiltradaComRemotas]);

    function desmontarGrafico() {
        if (graficoEconomiaMensal) graficoEconomiaMensal.dispose();
    }

    function atualizarGraficosEconomia() {
        //ECONOMIA  MENSAL E ANUAL -------------------------------------------------------------------------------------------------------------------------------------------------

        //monta uma lista com cada ano, e as UCs com valor no respectivo ao, para exibir no tooltip o valor de cada uc
        //mas isso só é exibido assim se é uma geradora com remotas, se for uma unica UC não mostra
        let quantidadeUCs = 0;
        props.listaGraficoGeracaoEconomia.forEach((itemMes) => itemMes.anos.forEach((itemAno) => (quantidadeUCs = Math.max(quantidadeUCs, (itemAno.unidades || []).length))));

        const unidadesPorAno = [];
        props.listaGraficoGeracaoEconomia.forEach((item) => {
            //faz copia profunda pra poder editar
            const itemMes = JSON.parse(JSON.stringify(item));

            itemMes.anos.forEach((itemAno) => {
                //só exibe nome das UCs ao passar o mouse e valida filtro secundário de UCs se a geradora filtrada no filtro principal, com suas respectivas ucs, for qtd > 1;

                const anoNaLista = unidadesPorAno.find((el) => el.ano == itemAno.ano);

                //atualiza o valor do ano com base nas UCs selecionadas, antes de incluir ou incrementar o total do ano
                const unidadesParaIncluir = (itemAno.unidades || []).filter((nova) => {
                    //se tem 2 uc ou mais no filtro interno de ucs ligadas a geradora, faz o detalhamento por uc, senão só acumula o total de cada ano
                    if (!props.umaUnicaFiltrada || quantidadeUCs <= 1) return true;

                    if (filtroUCs.find((el) => el.value == nova.id_unidade_consumidora)) {
                        return true;
                    } else {
                        //se não ta na lista filtrada desconsidera, e desconta do total
                        itemAno.valor -= nova.valor;
                        return false;
                    }
                });

                if (anoNaLista) {
                    anoNaLista.valor += itemAno.valor;

                    unidadesParaIncluir.forEach((nova) => {
                        const ucNaLista = anoNaLista.unidades.find((el) => el.nome_unidade_consumidora === nova.nome_unidade_consumidora);
                        if (ucNaLista) {
                            ucNaLista.competencia += ", " + nova.competencia;
                            ucNaLista.valor += nova.valor;
                        } else {
                            anoNaLista.unidades.push({
                                nome_unidade_consumidora: nova.nome_unidade_consumidora,
                                competencia: nova.competencia,
                                valor: nova.valor,
                            });
                        }
                    });
                } else {
                    unidadesPorAno.push({
                        ano: itemAno.ano,
                        valor: itemAno.valor,
                        unidades: unidadesParaIncluir.map((nova) => ({
                            nome_unidade_consumidora: nova.nome_unidade_consumidora,
                            competencia: nova.competencia,
                            valor: nova.valor,
                        })),
                    });
                }
            });
        });

        //adiciona tooltip para o gráfico de economia anual
        const unidadesPorAnoComTooltip = unidadesPorAno.map((item) => ({
            ...item,
            tooltip: item.unidades.reduce((acc, uc, i) => {
                const listaCompetencias = uc.competencia.split(", ");
                return (acc += `${i > 0 ? "\n\u200B\n" : ""}UC: [bold]${uc.nome_unidade_consumidora}[/]\n${listaCompetencias.length > 1 ? "Competências" : "Competência"}: [bold]${
                    listaCompetencias.length > 1 ? `${listaCompetencias[0]} à ${listaCompetencias[listaCompetencias.length - 1]}` : uc.competencia
                }[/]\nValor: [bold]R$ ${adicionarPontosMilhar(uc.valor)}[/]`);
            }, ""),
        }));

        const graficoEconomiaAnualAtualizado = montarGraficoClusteredColumnAnual({
            state: { cores },
            component: graficoEconomiaAnual,
            nome: "graficoEconomiaAnual",
            dados: [{ anos: unidadesPorAnoComTooltip }],
            titulo: "",
            prefixo: "R$",
            sufixo: "",
            negativo: false,
            ordenar: true,
            wrap: true,
            labelPersonalizado: props.umaUnicaFiltrada && quantidadeUCs > 1, // quando tem mais de uma mostra o tooltip personalizado com as UCs
        });
        setGraficoEconomiaAnual(graficoEconomiaAnualAtualizado);

        // usa quantidadeUCs para decidir como exibe o tooltip no grafico de economia anual e mensal
        const listaEconomiaMensalMapeada =
            props.umaUnicaFiltrada && quantidadeUCs > 1
                ? props.listaGraficoGeracaoEconomia.map((item) => {
                      //faz copia profunda pra poder editar
                      const itemMes = JSON.parse(JSON.stringify(item));
                      return {
                          ...itemMes,
                          anos: itemMes.anos.map((itemAno) => {
                              //atualiza o valor do ano com base nas UCs selecionadas, antes de incluir ou incrementar o total do ano
                              const unidadesParaIncluir = (itemAno.unidades || []).filter((nova) => {
                                  if (filtroUCs.find((el) => el.value == nova.id_unidade_consumidora)) {
                                      return true;
                                  } else {
                                      //se não ta na lista filtrada desconsidera, e desconta do total
                                      itemAno.valor -= nova.valor;
                                      return false;
                                  }
                              });

                              const { total, ucs } = unidadesParaIncluir.reduce(
                                  (acc, uc, i) => {
                                      //acumula o total do mes, somando o valor de cada uc
                                      acc.total += uc.valor;
                                      acc.ucs += i > 0 ? "\n\u200B\n" : "";

                                      acc.ucs += `UC: [bold]${uc.nome_unidade_consumidora}[/]]\nValor: [bold]R$ ${adicionarPontosMilhar(uc.valor)}[/]`;

                                      return acc;
                                  },
                                  { total: 0, ucs: "" }
                              );

                              const tooltip = `${ucs}\n\u200B\nValor Total: [bold]R$ ${adicionarPontosMilhar(total)}[/]`;
                              return { ...itemAno, tooltip };
                          }),
                      };
                  })
                : props.listaGraficoGeracaoEconomia;

        const graficoEconomiaMensalAtualizado = montarGraficoClusteredColumnMensal({
            state: { cores },
            component: graficoEconomiaMensal,
            nome: "graficoEconomiaMensal",
            dados: listaEconomiaMensalMapeada,
            titulo: "",
            prefixo: "R$",
            sufixo: "",
            fontColor: props.fontColor,
            labelPersonalizado: props.umaUnicaFiltrada && quantidadeUCs > 1, // quando tem mais de uma mostra o tooltip personalizado com as UCs,
        });
        setGraficoEconomiaMensal(graficoEconomiaMensalAtualizado);
    }

    //-----------------------------------------------------------------------------------------------------------------------------------------------------------
    let economia = 0;
    props.listaGraficoGeracaoEconomia.forEach((item, i) => {
        item.anos.forEach((ano, i) => {
            economia += parseFloat(ano.valor);
        });
    });

    return (
        <div ref={contentCardRef}>
            <ContentCard
                style={{
                    color: "#fff",
                    padding: 8,
                }}
            >
                <ContentCardBody
                    style={{
                        backgroundColor: props.backgroundColor || "#30303d",
                        textAlign: "center",
                    }}
                >
                    <div id="tituloGraficoEconomiaMensal" style={{ color: props.fontColor || "#fff", textAlign: "center" }}>
                        <div style={{ marginTop: 10, fontSize: 16 }}>Economia Mensal (R$)</div>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                marginTop: 20,
                            }}
                        >
                            <div
                                style={{
                                    color: "#FFF",
                                    backgroundColor: "#006d2e",
                                    width: 300,
                                    paddingTop: 8,
                                    paddingBottom: 8,
                                }}
                            >
                                Economia Total: R$ {adicionarPontosMilhar(economia || 0)}
                            </div>
                        </div>
                    </div>
                    <div style={{ width: "85vw", overflowX: "auto" }}>
                        <div
                            id="graficoEconomiaMensal"
                            style={{
                                width: "100%",
                                minWidth: "890px",
                                height: 400,
                                marginTop: -20,
                            }}
                        ></div>
                    </div>
                </ContentCardBody>
                <ContentCardBody
                    style={{
                        backgroundColor: props.backgroundColor || "#30303d",
                        textAlign: "center",
                    }}
                >
                    <div id="tituloGraficoEconomiaAnual" style={{ marginTop: 10, marginBottom: 20, fontSize: 16, color: props.fontColor || "#fff", textAlign: "center" }}>
                        <Row>
                            <Grid cols="12 4 4 4"></Grid>
                            <Grid cols="12 4 4 4" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                Economia Anual (R$)
                            </Grid>
                            <Grid cols="12 4 4 4">
                                {props.ucFiltradaComRemotas.length > 1 ? (
                                    <Row>
                                        <Grid cols="12 12 12 12">
                                            <Button
                                                title={exibirUCs ? "Ocultar UCs que compõem os gráficos" : "Exibir UCs que compõem os gráficos"}
                                                text={
                                                    <div>
                                                        {exibirUCs ? <i className="fas fa-eye-slash mr-2"></i> : <i className="fas fa-home mr-2"></i>}
                                                        {exibirUCs ? "Ocultar UCs que compõem os gráficos" : "Exibir UCs que compõem os gráficos"}
                                                    </div>
                                                }
                                                hoverStyle={{ filter: "brightness(1.1)" }}
                                                style={{
                                                    background: "linear-gradient(rgb(59 155 235), rgb(200 177 255))",
                                                    border: "2px solid #FFFFFF",
                                                    borderRadius: "0.3rem",
                                                    color: "#fff",
                                                    margin: window.innerWidth < 768 ? "0 0 2px 0" : "27px 0 2px 0",
                                                    padding: 8,
                                                }}
                                                disabled={false}
                                                event={() => {
                                                    setExibirUCs(!exibirUCs);
                                                }}
                                            />
                                        </Grid>
                                        {exibirUCs && (
                                            <Select
                                                style={{ backgroundColor: "#30303d", color: "#FFF", margin: 0, fontSize: 14 }}
                                                options={props.ucFiltradaComRemotas.filter((uc) => !filtroUCs.find((ucFiltro) => ucFiltro.value == uc.id))}
                                                label=""
                                                cols="12 12 12 12"
                                                placeholder="Selecione as UCs"
                                                isMulti={true}
                                                value={filtroUCs}
                                                onChange={(ucsSelecionadas) => {
                                                    setFiltroUCs([...ucsSelecionadas]);
                                                }}
                                            />
                                        )}
                                    </Row>
                                ) : null}
                            </Grid>
                        </Row>
                    </div>
                    <div style={{ width: "85vw", overflowX: "auto" }}>
                        <div
                            id="graficoEconomiaAnual"
                            style={{
                                width: "100%",
                                minWidth: "890px",
                                height: 400,
                                marginTop: -20,
                            }}
                        ></div>
                    </div>
                </ContentCardBody>
            </ContentCard>
        </div>
    );
}
